import { useAuthStore } from '../store/authStore';

type Envelope<T> =
  | { success: true; data: T }
  | { success: false; error: { message: string } };

export function apiGet<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'GET' });
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiDelete<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' });
}

/** 인증 헤더로 바이너리(이미지 등)를 받아 Blob 으로 반환. <img> 가 헤더를 못 보내는 경우용. */
export async function apiGetBlob(path: string): Promise<Blob> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(path, { headers });
  if (res.status === 401 && token) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
    throw new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export async function apiRequest<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const token = useAuthStore.getState().token;
  // FormData(파일 업로드)는 브라우저가 multipart 경계를 붙인 Content-Type 을 직접 넣는다
  const isForm = typeof FormData !== 'undefined' && options?.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...((options?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(path, { ...options, headers });

  // 활성 세션(토큰 보유)에서의 401만 만료로 간주해 강제 로그아웃·리다이렉트한다.
  // 로그인처럼 토큰 없는 요청의 401(잘못된 자격증명·승인 대기 등)은 서버 메시지를
  // 그대로 전달해 화면에서 안내하도록 둔다. (페이지 리로드로 알림이 닫히는 문제 방지)
  if (res.status === 401 && token) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
    throw new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
  }

  // 프록시(nginx)가 막으면 JSON 이 아닌 HTML 오류 페이지가 온다 — 그대로 파싱하면 알아보기 힘든 오류가 난다
  let json: Envelope<T>;
  try {
    json = (await res.json()) as Envelope<T>;
  } catch {
    throw new Error(
      res.status === 413 ? '파일이 너무 큽니다 (HTTP 413)' : `서버 응답을 읽지 못했습니다 (HTTP ${res.status})`,
    );
  }
  if (!json.success) throw new Error(json.error?.message ?? `HTTP ${res.status}`);
  return json.data;
}
